// Kampanya kurallarının tek kaynağı. Hem satış ekranındaki gösterim hem de
// siparişe gönderilen satırlar bu hesaplardan üretilir.
//
// Öncelik sırası: kampanya fiyatı > birlikte alım indirimi > hediye.
// Bir üründe birden fazla kampanya alanı dolu olsa bile yalnızca en öncelikli
// olan teklif edilir ve uygulanması için temsilcinin açıkça seçmesi gerekir.

export const CAMPAIGN_PRICE = "price";
export const CAMPAIGN_BUNDLE = "bundle";
export const CAMPAIGN_GIFT = "gift";

const toFixedIfNecessary = (value, dp) => +parseFloat(value).toFixed(dp);

export const splitCampaignCodes = (value) =>
  value
    ? String(value)
        .split(";")
        .map((code) => code.trim())
        .filter((code) => code !== "")
    : [];

// Kalan kampanya kontenjanı. null => sınırsız (stok bitene kadar)
export const getCampaignRemaining = (product) => {
  if (!product) {
    return 0;
  }

  if (
    product.campaignQuantity === null ||
    product.campaignQuantity === undefined
  ) {
    return null;
  }

  const remaining =
    product.campaignRemaining !== undefined &&
    product.campaignRemaining !== null
      ? product.campaignRemaining
      : product.campaignQuantity - (product.campaignUsed || 0);

  return Math.max(remaining, 0);
};

// Bu ürünün kampanyası, sepetteki başka bir ürünün stok koduna bağlı mı?
export const isBundleActive = (line, products) => {
  const codes = splitCampaignCodes(line?.product?.campaignBarcode);

  if (codes.length === 0) {
    return false;
  }

  return (products || []).some(
    (other) =>
      other.uid !== line.uid &&
      other.product?.code &&
      codes.includes(String(other.product.code)),
  );
};

export const getCampaignType = (product, bundleActive) => {
  if (!product) {
    return null;
  }

  const remaining = getCampaignRemaining(product);

  if (product.campaignPrice && (remaining === null || remaining > 0)) {
    return CAMPAIGN_PRICE;
  }

  if (product.campaignBundleDiscount && bundleActive) {
    return CAMPAIGN_BUNDLE;
  }

  if (product.campaignGiftQuantity) {
    return CAMPAIGN_GIFT;
  }

  return null;
};

const applyLineDiscount = (unitPrice, line) => {
  const discount = Number(line?.discount) || 0;

  if (!discount) {
    return unitPrice;
  }

  return line.discountType === "percent"
    ? toFixedIfNecessary(((100 - discount) / 100) * unitPrice, 2)
    : unitPrice - discount;
};

// Hediye açıkken, stoğa sığacak en fazla ücretli adet.
// p + floor(p / n) <= stok eşitsizliğinin en büyük p çözümü.
export const getMaxPaidQuantity = (stock, giftPer) => {
  const available = Math.max(Math.floor(stock) || 0, 0);

  if (!giftPer || giftPer <= 0) {
    return available;
  }

  const groups = Math.floor(available / (giftPer + 1));
  const rest = Math.min(available - groups * (giftPer + 1), giftPer - 1);

  return groups * giftPer + rest;
};

// Tek bir satırın kampanya kırılımı. rows dizisi doğrudan siparişe gönderilir.
export const buildLine = (line, bundleActive) => {
  const product = line?.product;
  const quantity = Math.max(Math.floor(Number(line?.quantity) || 0), 0);
  const remaining = getCampaignRemaining(product);
  const type = getCampaignType(product, bundleActive);
  const applied = Boolean(line?.useCampaign && type && quantity > 0);

  const base = {
    type,
    applied,
    bundleActive: Boolean(bundleActive),
    remaining,
    giftQuantity: 0,
    campaignQuantity: 0,
    normalQuantity: quantity,
    totalQuantity: quantity,
    unitPrice: 0,
    totalAmount: 0,
    rows: [],
  };

  if (!product || quantity <= 0) {
    return base;
  }

  const normalUnit = applyLineDiscount(product.price, line);

  if (!applied) {
    return {
      ...base,
      unitPrice: normalUnit,
      totalAmount: normalUnit * quantity,
      rows: [{ quantity, price: normalUnit, isCampaign: false }],
    };
  }

  if (type === CAMPAIGN_PRICE) {
    // Kontenjan yetmiyorsa satır ikiye bölünür: kalan kadarı kampanya
    // fiyatından, üstü normal fiyattan
    const campaignQuantity =
      remaining === null ? quantity : Math.min(quantity, remaining);
    const normalQuantity = quantity - campaignQuantity;
    const campaignUnit = applyLineDiscount(product.campaignPrice, line);

    const rows = [];

    if (campaignQuantity > 0) {
      rows.push({
        quantity: campaignQuantity,
        price: campaignUnit,
        isCampaign: true,
      });
    }

    if (normalQuantity > 0) {
      rows.push({
        quantity: normalQuantity,
        price: normalUnit,
        isCampaign: false,
      });
    }

    const totalAmount =
      campaignQuantity * campaignUnit + normalQuantity * normalUnit;

    return {
      ...base,
      campaignQuantity,
      normalQuantity,
      totalAmount,
      unitPrice: totalAmount / quantity,
      rows,
    };
  }

  if (type === CAMPAIGN_BUNDLE) {
    const bundleUnit = applyLineDiscount(
      product.price * (1 - product.campaignBundleDiscount / 100),
      line,
    );

    return {
      ...base,
      unitPrice: bundleUnit,
      totalAmount: bundleUnit * quantity,
      rows: [{ quantity, price: bundleUnit, isCampaign: false }],
    };
  }

  // Hediye: satır tek kalır, hediye adedi eklenir ve birim fiyat yayılır
  const giftQuantity = Math.floor(quantity / product.campaignGiftQuantity);
  const totalQuantity = quantity + giftQuantity;
  const totalAmount = normalUnit * quantity;
  const unitPrice =
    totalQuantity > 0 ? totalAmount / totalQuantity : normalUnit;

  return {
    ...base,
    giftQuantity,
    normalQuantity: totalQuantity,
    totalQuantity,
    unitPrice,
    totalAmount,
    rows: [{ quantity: totalQuantity, price: unitPrice, isCampaign: false }],
  };
};

export const buildOrderLines = (products) =>
  (products || []).map((line) =>
    buildLine(line, isBundleActive(line, products)),
  );

export const getOrderTotal = (products) =>
  buildOrderLines(products).reduce(
    (total, breakdown) => total + breakdown.totalAmount,
    0,
  );
